import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortal } from '../context/PortalContext';
import { clubService } from '../services/clubService';
import { ClubAnalyticsOverviewPayload } from '../types';
import { formatCompactCurrency } from '../utils/formatters';

type RangePreset = 'today' | 'week' | 'month' | 'custom';
type GroupBy = 'day' | 'week' | 'month';
type ChartTone = 'rake' | 'deposit' | 'withdrawal' | 'balance' | 'fee';
type AnalyticsPanel = 'rake' | 'cashflow' | 'tracked' | 'fees';
type TooltipPillTone = 'neutral' | 'deposit' | 'withdrawal';

type MetricTrendCardProps = {
  eyebrow: string;
  title: string;
  value: number;
  helper: string;
  tone: ChartTone;
  trendValues: number[];
};

type SingleChartPoint = {
  key: string;
  label: string;
  value: number;
  tooltipSections: ChartTooltipSection[];
};

type GroupedChartPoint = {
  key: string;
  label: string;
  primaryValue: number;
  secondaryValue: number;
  tooltipSections: ChartTooltipSection[];
};

type ChartTooltipPill = {
  label: string;
  tone?: TooltipPillTone;
};

type ChartTooltipSection = {
  label?: string;
  pills: ChartTooltipPill[];
};

type ChartTooltipState = {
  title: string;
  x: number;
  y: number;
  sections: ChartTooltipSection[];
};

const GRID_STOPS = [1, 0.75, 0.5, 0.25];

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toLocalTimestamp = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const getRangeForPreset = (preset: RangePreset): { from: string; to: string } => {
  const now = new Date();
  const from = new Date(now);
  const to = new Date(now);

  if (preset === 'today') {
    from.setHours(0, 0, 0, 0);
  } else if (preset === 'week') {
    const weekday = now.getDay() === 0 ? 6 : now.getDay() - 1;
    from.setDate(now.getDate() - weekday);
    from.setHours(0, 0, 0, 0);
  } else if (preset === 'month') {
    from.setDate(1);
    from.setHours(0, 0, 0, 0);
  }

  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  };
};

const getGroupByLabel = (groupBy: GroupBy): string => {
  if (groupBy === 'week') return 'semana';
  if (groupBy === 'month') return 'mes';
  return 'dia';
};

const getBarHeight = (value: number, maxValue: number): string => {
  if (maxValue <= 0 || value <= 0) return '0%';
  const percentage = (value / maxValue) * 100;
  return `${Math.max(8, Math.min(100, percentage)).toFixed(2)}%`;
};

const getScrollableMinWidth = (count: number, columnWidth = 88): string => {
  return `${Math.max(520, count * columnWidth)}px`;
};

const formatCompactChartValue = (value: number): string => {
  const safe = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('pt-BR', {
    notation: Math.abs(safe) >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(safe) >= 1000 ? 1 : 2,
  }).format(safe);
};

const formatPlainAmount = (value: number): string => {
  const safe = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safe);
};

const getTooltipPosition = (element: HTMLElement): { x: number; y: number } => {
  const rect = element.getBoundingClientRect();
  const assumedWidth = 228;
  const viewportPadding = 20;
  const minX = viewportPadding + assumedWidth / 2;
  const maxX = window.innerWidth - viewportPadding - assumedWidth / 2;

  return {
    x: Math.min(maxX, Math.max(minX, rect.left + rect.width / 2)),
    y: Math.max(22, rect.top - 12),
  };
};

const FloatingChartTooltip: React.FC<{ tooltip: ChartTooltipState | null }> = ({ tooltip }) => {
  if (!tooltip) return null;

  return createPortal(
    <div
      className="portal-analytics-floating-tooltip"
      style={{ left: tooltip.x, top: tooltip.y }}
      role="presentation"
    >
      <strong>{tooltip.title}</strong>

      {tooltip.sections.map((section, index) => (
        <div key={`${tooltip.title}-${index}`} className="portal-analytics-floating-tooltip__section">
          {section.label ? <em>{section.label}</em> : null}

          <div className="portal-analytics-floating-tooltip__pills">
            {section.pills.map((pill) => (
              <span
                key={`${tooltip.title}-${index}-${pill.label}`}
                className={[
                  'portal-analytics-floating-tooltip__pill',
                  pill.tone ? `portal-analytics-floating-tooltip__pill--${pill.tone}` : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {pill.label}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>,
    document.body
  );
};

const useChartTooltip = () => {
  const [tooltip, setTooltip] = useState<ChartTooltipState | null>(null);

  const hideTooltip = useCallback(() => {
    setTooltip(null);
  }, []);

  const showTooltip = useCallback(
    (element: HTMLElement, title: string, sections: ChartTooltipSection[]) => {
      const position = getTooltipPosition(element);
      setTooltip({
        title,
        sections,
        ...position,
      });
    },
    []
  );

  useEffect(() => {
    if (!tooltip) return;

    const closeTooltip = () => setTooltip(null);
    window.addEventListener('scroll', closeTooltip, true);
    window.addEventListener('resize', closeTooltip);

    return () => {
      window.removeEventListener('scroll', closeTooltip, true);
      window.removeEventListener('resize', closeTooltip);
    };
  }, [tooltip]);

  return {
    tooltip,
    showTooltip,
    hideTooltip,
  };
};

const MetricTrendCard: React.FC<MetricTrendCardProps> = ({ eyebrow, title, value, helper, tone, trendValues }) => {
  const safeTrend = trendValues.length ? trendValues.slice(-8) : [0];
  const maxTrendValue = Math.max(...safeTrend, 0);

  return (
    <article className={`portal-analytics-metric-card portal-analytics-metric-card--${tone}`}>
      <div className="portal-analytics-metric-card__header">
        <p className="portal-section-card__eyebrow">{eyebrow}</p>
        <span className="portal-analytics-metric-card__badge">{title}</span>
      </div>

      <div className="portal-analytics-metric-card__content">
        <strong>{formatCompactCurrency(value)}</strong>
        <span>{helper}</span>
      </div>

      <div className="portal-analytics-mini-bars" aria-hidden="true">
        {safeTrend.map((point, index) => (
          <span
            key={`${tone}-${index}`}
            className={`portal-analytics-mini-bars__bar portal-analytics-mini-bars__bar--${tone}`}
            style={{ height: getBarHeight(point, maxTrendValue) }}
          />
        ))}
      </div>
    </article>
  );
};

const SingleSeriesChart: React.FC<{
  eyebrow: string;
  title: string;
  tone: ChartTone;
  points: SingleChartPoint[];
  emptyMessage: string;
}> = ({ eyebrow, title, tone, points, emptyMessage }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { tooltip, showTooltip, hideTooltip } = useChartTooltip();

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const frame = window.requestAnimationFrame(() => {
      element.scrollLeft = element.scrollWidth;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [points]);

  if (!points.length) {
    return (
      <section className="portal-section-card">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">{eyebrow}</p>
            <h3>{title}</h3>
          </div>
        </div>
        <p className="portal-muted">{emptyMessage}</p>
      </section>
    );
  }

  const maxValue = Math.max(...points.map((point) => point.value), 0);

  return (
    <section className="portal-section-card">
      <div className="portal-section-card__header">
        <div>
          <p className="portal-section-card__eyebrow">{eyebrow}</p>
          <h3>{title}</h3>
        </div>
      </div>

      <div ref={scrollRef} className="portal-analytics-chart-scroll">
        <div className="portal-analytics-chart-shell" style={{ minWidth: getScrollableMinWidth(points.length) }}>
          <div className="portal-analytics-chart-grid">
            {GRID_STOPS.map((stop) => (
              <span key={`${title}-${stop}`} style={{ bottom: `${stop * 100}%` }} />
            ))}
          </div>

          <div className="portal-analytics-chart-columns">
            {points.map((point) => (
              <article
                key={point.key}
                className="portal-analytics-chart-column"
                tabIndex={0}
                onMouseEnter={(event) => showTooltip(event.currentTarget, point.label, point.tooltipSections)}
                onMouseLeave={hideTooltip}
                onFocus={(event) => showTooltip(event.currentTarget, point.label, point.tooltipSections)}
                onBlur={hideTooltip}
              >
                <div className="portal-analytics-chart-column__value">
                  {formatCompactChartValue(point.value)}
                </div>
                <div className="portal-analytics-chart-column__stage">
                  <div
                    className={`portal-analytics-chart-column__bar portal-analytics-chart-column__bar--${tone}`}
                    style={{ height: getBarHeight(point.value, maxValue) }}
                  />
                </div>
                <strong className="portal-analytics-chart-column__label">{point.label}</strong>
              </article>
            ))}
          </div>
        </div>
      </div>

      <FloatingChartTooltip tooltip={tooltip} />
    </section>
  );
};

const GroupedSeriesChart: React.FC<{
  eyebrow: string;
  title: string;
  points: GroupedChartPoint[];
  emptyMessage: string;
}> = ({ eyebrow, title, points, emptyMessage }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { tooltip, showTooltip, hideTooltip } = useChartTooltip();

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const frame = window.requestAnimationFrame(() => {
      element.scrollLeft = element.scrollWidth;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [points]);

  if (!points.length) {
    return (
      <section className="portal-section-card">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">{eyebrow}</p>
            <h3>{title}</h3>
          </div>
        </div>
        <p className="portal-muted">{emptyMessage}</p>
      </section>
    );
  }

  const maxValue = Math.max(
    ...points.flatMap((point) => [point.primaryValue, point.secondaryValue]),
    0
  );

  return (
    <section className="portal-section-card">
      <div className="portal-section-card__header">
        <div>
          <p className="portal-section-card__eyebrow">{eyebrow}</p>
          <h3>{title}</h3>
        </div>
        <div className="portal-analytics-legend">
          <span className="portal-analytics-legend__item">
            <i className="portal-analytics-legend__dot portal-analytics-legend__dot--deposit" />
            Depositos
          </span>
          <span className="portal-analytics-legend__item">
            <i className="portal-analytics-legend__dot portal-analytics-legend__dot--withdrawal" />
            Saques
          </span>
        </div>
      </div>

      <div ref={scrollRef} className="portal-analytics-chart-scroll">
        <div className="portal-analytics-chart-shell" style={{ minWidth: getScrollableMinWidth(points.length, 108) }}>
          <div className="portal-analytics-chart-grid">
            {GRID_STOPS.map((stop) => (
              <span key={`${title}-${stop}`} style={{ bottom: `${stop * 100}%` }} />
            ))}
          </div>

          <div className="portal-analytics-chart-columns portal-analytics-chart-columns--grouped">
            {points.map((point) => (
              <article
                key={point.key}
                className="portal-analytics-chart-column portal-analytics-chart-column--grouped"
                tabIndex={0}
                onMouseEnter={(event) => showTooltip(event.currentTarget, point.label, point.tooltipSections)}
                onMouseLeave={hideTooltip}
                onFocus={(event) => showTooltip(event.currentTarget, point.label, point.tooltipSections)}
                onBlur={hideTooltip}
              >
                <div className="portal-analytics-chart-column__value">
                  <span>{formatCompactChartValue(point.primaryValue)}</span>
                  <span>{formatCompactChartValue(point.secondaryValue)}</span>
                </div>
                <div className="portal-analytics-chart-column__stage portal-analytics-chart-column__stage--grouped">
                  <div
                    className="portal-analytics-chart-column__bar portal-analytics-chart-column__bar--deposit"
                    style={{ height: getBarHeight(point.primaryValue, maxValue) }}
                  />
                  <div
                    className="portal-analytics-chart-column__bar portal-analytics-chart-column__bar--withdrawal"
                    style={{ height: getBarHeight(point.secondaryValue, maxValue) }}
                  />
                </div>
                <strong className="portal-analytics-chart-column__label">{point.label}</strong>
              </article>
            ))}
          </div>
        </div>
      </div>

      <FloatingChartTooltip tooltip={tooltip} />
    </section>
  );
};

const AnalyticsPage: React.FC = () => {
  const { selectedClub } = usePortal();
  const [rangePreset, setRangePreset] = useState<RangePreset>('month');
  const [groupBy, setGroupBy] = useState<GroupBy>('day');
  const [activePanel, setActivePanel] = useState<AnalyticsPanel>('rake');
  const [fromDate, setFromDate] = useState<string>(() => getRangeForPreset('month').from);
  const [toDate, setToDate] = useState<string>(() => getRangeForPreset('month').to);
  const [analytics, setAnalytics] = useState<ClubAnalyticsOverviewPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (rangePreset === 'custom') return;

    const nextRange = getRangeForPreset(rangePreset);
    setFromDate(nextRange.from);
    setToDate(nextRange.to);
  }, [rangePreset]);

  useEffect(() => {
    if (!selectedClub?.id) {
      setAnalytics(null);
      setError('');
      return;
    }

    if (!fromDate || !toDate) return;

    const from = new Date(`${fromDate}T00:00:00`);
    const to = new Date(`${toDate}T23:59:59`);

    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
      setError('Periodo invalido para analytics do club');
      return;
    }

    if (from.getTime() > to.getTime()) {
      setError('A data inicial nao pode ser maior que a data final');
      return;
    }

    setLoading(true);
    setError('');

    clubService
      .getAnalyticsOverview(selectedClub.id, {
        from: toLocalTimestamp(from),
        to: toLocalTimestamp(to),
        groupBy,
      })
      .then(setAnalytics)
      .catch((nextError: any) => setError(nextError.message || 'Erro ao carregar analytics do club'))
      .finally(() => setLoading(false));
  }, [selectedClub?.id, fromDate, toDate, groupBy]);

  const metricCards = useMemo(
    () => [
      {
        eyebrow: 'Rake rastreado',
        title: 'Rake',
        value: analytics?.summary.total_rake || 0,
        helper: `${analytics?.summary.total_hands || 0} maos no periodo`,
        tone: 'rake' as ChartTone,
        trendValues: analytics?.rake_series.map((point) => point.total_rake) || [],
      },
      {
        eyebrow: 'Entradas externas',
        title: 'Depositos',
        value: analytics?.summary.deposit_gross_total || 0,
        helper: `${analytics?.summary.deposit_count || 0} operacoes conciliadas`,
        tone: 'deposit' as ChartTone,
        trendValues: analytics?.cashflow_series.map((point) => point.deposit_gross_total) || [],
      },
      {
        eyebrow: 'Saidas externas',
        title: 'Saques',
        value: analytics?.summary.withdrawal_gross_total || 0,
        helper: `${analytics?.summary.withdrawal_count || 0} operacoes processadas`,
        tone: 'withdrawal' as ChartTone,
        trendValues: analytics?.cashflow_series.map((point) => point.withdrawal_gross_total) || [],
      },
      {
        eyebrow: 'Carteiras rastreadas',
        title: 'Saldos vivos',
        value: analytics?.summary.current_tracked_balance_total || 0,
        helper: `${analytics?.summary.wallets_with_balance || 0} jogadores com saldo`,
        tone: 'balance' as ChartTone,
        trendValues: analytics?.tracked_balance_series.map((point) => point.tracked_balance_total) || [],
      },
      {
        eyebrow: 'Custo do gateway',
        title: 'Taxas Asaas',
        value: analytics?.summary.total_asaas_fee || 0,
        helper: `${analytics?.summary.fee_operations_count || 0} operacoes com taxa`,
        tone: 'fee' as ChartTone,
        trendValues: analytics?.fee_series.map((point) => point.total_fee) || [],
      },
    ],
    [analytics]
  );

  const rakePoints = useMemo<SingleChartPoint[]>(
    () =>
      (analytics?.rake_series || []).map((point) => ({
        key: point.period_start,
        label: point.period_label,
        value: point.total_rake,
        tooltipSections: [
          {
            pills: [
              { label: `Valor ${formatPlainAmount(point.total_rake)}` },
              { label: `${point.hands_played} maos` },
              { label: `${point.raked_hands} com rake` },
            ],
          },
        ],
      })),
    [analytics?.rake_series]
  );

  const cashflowPoints = useMemo<GroupedChartPoint[]>(
    () =>
      (analytics?.cashflow_series || []).map((point) => ({
        key: point.period_start,
        label: point.period_label,
        primaryValue: point.deposit_gross_total,
        secondaryValue: point.withdrawal_gross_total,
        tooltipSections: [
          {
            label: 'Depositos',
            pills: [
              { label: `Valor ${formatPlainAmount(point.deposit_gross_total)}`, tone: 'deposit' },
              { label: `${point.deposit_count} operacoes` },
              { label: `Liquido ${formatPlainAmount(point.deposit_net_total)}` },
            ],
          },
          {
            label: 'Saques',
            pills: [
              { label: `Valor ${formatPlainAmount(point.withdrawal_gross_total)}`, tone: 'withdrawal' },
              { label: `${point.withdrawal_count} operacoes` },
              { label: `Liquido ${formatPlainAmount(point.withdrawal_net_total)}` },
            ],
          },
        ],
      })),
    [analytics?.cashflow_series]
  );

  const trackedBalancePoints = useMemo<SingleChartPoint[]>(
    () =>
      (analytics?.tracked_balance_series || []).map((point) => ({
        key: point.period_start,
        label: point.period_label,
        value: point.tracked_balance_total,
        tooltipSections: [
          {
            pills: [
              { label: `Valor ${formatPlainAmount(point.tracked_balance_total)}` },
              { label: `${point.wallets_with_balance} com saldo` },
              { label: `${point.wallets_tracked} carteiras` },
            ],
          },
        ],
      })),
    [analytics?.tracked_balance_series]
  );

  const feePoints = useMemo<SingleChartPoint[]>(
    () =>
      (analytics?.fee_series || []).map((point) => ({
        key: point.period_start,
        label: point.period_label,
        value: point.total_fee,
        tooltipSections: [
          {
            pills: [
              { label: `Valor ${formatPlainAmount(point.total_fee)}` },
              { label: `Depositos ${formatPlainAmount(point.deposit_fee_total)}` },
              { label: `Saques ${formatPlainAmount(point.withdrawal_fee_total)}` },
            ],
          },
        ],
      })),
    [analytics?.fee_series]
  );

  const activeChartPanel = useMemo(() => {
    if (activePanel === 'cashflow') {
      return (
        <GroupedSeriesChart
          eyebrow="Depositos e saques"
          title="Fluxo externo do club"
          points={cashflowPoints}
          emptyMessage="Nenhuma operacao externa encontrada para o periodo selecionado."
        />
      );
    }

    if (activePanel === 'tracked') {
      return (
        <SingleSeriesChart
          eyebrow="Saldos rastreados"
          title="Montante consolidado nas carteiras"
          tone="balance"
          points={trackedBalancePoints}
          emptyMessage="Nenhum saldo rastreado disponivel para o periodo selecionado."
        />
      );
    }

    if (activePanel === 'fees') {
      return (
        <SingleSeriesChart
          eyebrow="Custos do gateway"
          title={`Taxas Asaas por ${getGroupByLabel(groupBy)}`}
          tone="fee"
          points={feePoints}
          emptyMessage="Nenhum custo Asaas encontrado para o periodo selecionado."
        />
      );
    }

    return (
      <SingleSeriesChart
        eyebrow="Grafico de rake"
        title={`Rake em colunas por ${getGroupByLabel(groupBy)}`}
        tone="rake"
        points={rakePoints}
        emptyMessage="Nenhum rake encontrado para o periodo selecionado."
      />
    );
  }, [activePanel, cashflowPoints, feePoints, groupBy, rakePoints, trackedBalancePoints]);

  return (
    <div className="portal-page-stack portal-page-stack--analytics-dashboard">
      <section className="portal-section-card portal-report-toolbar portal-report-toolbar--analytics">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Leitura executiva</p>
            <h3>Analytics consolidados do club</h3>
          </div>
          <div className="portal-section-card__header-actions">
            <span className="portal-muted">Rake, fluxo externo, saldos rastreados e custos Asaas</span>
          </div>
        </div>

        <div className="portal-pill-group">
          <button
            type="button"
            className={`portal-pill-button${rangePreset === 'today' ? ' portal-pill-button--active' : ''}`}
            onClick={() => setRangePreset('today')}
          >
            Hoje
          </button>
          <button
            type="button"
            className={`portal-pill-button${rangePreset === 'week' ? ' portal-pill-button--active' : ''}`}
            onClick={() => setRangePreset('week')}
          >
            Semana
          </button>
          <button
            type="button"
            className={`portal-pill-button${rangePreset === 'month' ? ' portal-pill-button--active' : ''}`}
            onClick={() => setRangePreset('month')}
          >
            Mes
          </button>
          <button
            type="button"
            className={`portal-pill-button${rangePreset === 'custom' ? ' portal-pill-button--active' : ''}`}
            onClick={() => setRangePreset('custom')}
          >
            Personalizado
          </button>
        </div>

        <div className="portal-form-grid portal-form-grid--analytics">
          <label>
            <span>Data inicial</span>
            <input
              type="date"
              value={fromDate}
              onChange={(event) => {
                setRangePreset('custom');
                setFromDate(event.target.value);
              }}
            />
          </label>

          <label>
            <span>Data final</span>
            <input
              type="date"
              value={toDate}
              onChange={(event) => {
                setRangePreset('custom');
                setToDate(event.target.value);
              }}
            />
          </label>

          <label>
            <span>Agrupar por</span>
            <select value={groupBy} onChange={(event) => setGroupBy(event.target.value as GroupBy)}>
              <option value="day">Dia</option>
              <option value="week">Semana</option>
              <option value="month">Mes</option>
            </select>
          </label>
        </div>
      </section>

      {error ? <div className="portal-banner portal-banner--error">{error}</div> : null}
      {loading ? <div className="portal-loading-screen">Carregando analytics do club...</div> : null}

      <section className="portal-analytics-metric-grid portal-analytics-metric-grid--compact">
        {metricCards.map((card) => (
          <MetricTrendCard key={card.title} {...card} />
        ))}
      </section>

      <section className="portal-section-card portal-section-card--analytics-panel">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Painel principal</p>
            <h3>Leitura grafica do club</h3>
          </div>
          <div className="portal-pill-group">
            <button
              type="button"
              className={`portal-pill-button${activePanel === 'rake' ? ' portal-pill-button--active' : ''}`}
              onClick={() => setActivePanel('rake')}
            >
              Rake
            </button>
            <button
              type="button"
              className={`portal-pill-button${activePanel === 'cashflow' ? ' portal-pill-button--active' : ''}`}
              onClick={() => setActivePanel('cashflow')}
            >
              Depositos e saques
            </button>
            <button
              type="button"
              className={`portal-pill-button${activePanel === 'tracked' ? ' portal-pill-button--active' : ''}`}
              onClick={() => setActivePanel('tracked')}
            >
              Saldos rastreados
            </button>
            <button
              type="button"
              className={`portal-pill-button${activePanel === 'fees' ? ' portal-pill-button--active' : ''}`}
              onClick={() => setActivePanel('fees')}
            >
              Taxas Asaas
            </button>
          </div>
        </div>

        <div className="portal-analytics-panel__body">{activeChartPanel}</div>
      </section>
    </div>
  );
};

export default AnalyticsPage;
